import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API } from '../utils/helpers';
import useMobileViewport from '../utils/useMobileViewport';

// ── The damage hold page: /rental-hold?token=… (4 Oct) ─────────────────────────
//
// Texted to the customer before their rental bikes go out, or opened on the
// counter computer for them to tap a card. The token is the credential, like a
// pay link. Up to the shop's day limit it is a hold, released when the bikes
// come back clean; longer rentals are charged and refunded on return.

function HoldForm({ amount, mode, token, accent, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    if (!stripe || !elements) return;
    setBusy(true); setError('');
    const { error: e, paymentIntent } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (e) { setError(e.message || 'Your card could not be used. Please try another.'); setBusy(false); return; }
    if (paymentIntent && ['requires_capture', 'succeeded'].includes(paymentIntent.status)) {
      await fetch(`${API}/book/rent/hold/${token}/confirm`, { method: 'POST' }).catch(() => {});
      onDone();
      return;
    }
    setError('That did not go through. Please try again or ask at the counter.');
    setBusy(false);
  };
  return (
    <>
      <PaymentElement options={{ wallets: { link: 'never' } }} />
      {error && <div style={{ fontSize: 13, color: '#c0392b', marginTop: 12 }}>{error}</div>}
      <button onClick={go} disabled={!stripe || busy}
        style={{ width: '100%', background: accent, color: '#fff', border: 'none', padding: 14, borderRadius: 10, fontSize: 15, fontWeight: 600, marginTop: 16, fontFamily: 'inherit', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? 'Processing…' : mode === 'charge' ? `Pay $${amount.toFixed(2)} deposit` : `Place $${amount.toFixed(2)} hold`}
      </button>
    </>
  );
}

export default function RentalHoldPage() {
  useMobileViewport();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token'), []);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!token) { setFailed(true); return; }
    fetch(`${API}/book/rent/hold/${token}`).then(r => { if (!r.ok) throw new Error(); return r.json(); }).then(setData).catch(() => setFailed(true));
  }, [token]);
  const stripePromise = useMemo(() => (data && data.state === 'payable' && data.stripePublishableKey
    ? loadStripe(data.stripePublishableKey, { stripeAccount: data.stripeAccountId }) : null), [data]);

  const accent = (data && /^#[0-9a-fA-F]{6}$/.test(data.accentColor || '')) ? data.accentColor : '#e67e22';
  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: '#f7f6f3', padding: '24px 12px', fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 440, margin: '0 auto', background: '#fff', border: '1px solid #e2e5ea', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ background: '#0f1724', padding: '22px 22px 18px', textAlign: 'center' }}>
          {data?.shopLogoUrl
            ? <img src={data.shopLogoUrl} alt={data.shopName || ''} style={{ maxHeight: 56, maxWidth: 260, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            : <div style={{ fontSize: 17, fontWeight: 600, color: '#fff' }}>{data?.shopName || 'Your rental'}</div>}
          <div style={{ fontSize: 12, color: '#9aa4b2', marginTop: 6 }}>Damage {data?.mode === 'charge' ? 'deposit' : 'hold'} for your rental</div>
        </div>
        <div style={{ height: 3, background: accent }} />
        <div style={{ padding: 22 }}>{children}</div>
      </div>
      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: '#9aa4b2' }}>Powered by Spinboard</div>
    </div>
  );

  if (failed) return shell(<div style={{ textAlign: 'center', color: '#5b6472', fontSize: 14, padding: '20px 0' }}>We couldn't find this link. Please ask at the shop.</div>);
  if (!data) return shell(<div style={{ textAlign: 'center', color: '#5b6472', fontSize: 14, padding: '20px 0' }}>Loading…</div>);

  const summary = (
    <div style={{ background: '#f7f6f3', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#5b6472', lineHeight: 1.6 }}>
      <div style={{ fontWeight: 600, color: '#1a2233' }}>{data.bikes.join(', ')}</div>
      <div>{data.whenText}</div>
    </div>
  );
  if (done || data.state === 'done') return shell(
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#eafaf1', color: '#27ae60', fontSize: 26, lineHeight: '52px', margin: '0 auto 14px' }}>✓</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#1a2233', marginBottom: 8 }}>{data.mode === 'charge' ? 'Deposit paid' : 'Hold placed'}</div>
      {summary}
      <div style={{ fontSize: 13, color: '#5b6472', lineHeight: 1.6 }}>
        {data.mode === 'charge'
          ? `We'll refund the $${data.amount.toFixed(2)} when the bikes come back in good shape.`
          : `Nothing is charged. The $${data.amount.toFixed(2)} hold is released when the bikes come back in good shape.`} Enjoy the ride!
      </div>
    </div>
  );
  if (data.state === 'closed') return shell(<div style={{ textAlign: 'center', color: '#5b6472', fontSize: 14 }}>{summary}This rental is closed. Nothing more is needed.</div>);
  if (data.state !== 'payable' || !stripePromise) return shell(<div style={{ textAlign: 'center', color: '#5b6472', fontSize: 14 }}>{summary}This link can't take a card right now. Please ask at the shop{data.shopPhone ? ` (${data.shopPhone})` : ''}.</div>);

  return shell(
    <>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#1a2233', marginBottom: 4 }}>Hi {(data.customerName || '').trim().split(/\s+/)[0] || 'there'}</div>
      <div style={{ fontSize: 13, color: '#5b6472', marginBottom: 14, lineHeight: 1.55 }}>
        {data.mode === 'charge'
          ? `For rentals this long we take a $${data.amount.toFixed(2)} damage deposit now, and refund it when the bikes come back in good shape.`
          : `Before your bikes go out we place a $${data.amount.toFixed(2)} hold on your card for damage. Nothing is charged; it is released when the bikes come back in good shape.`}
      </div>
      {summary}
      <Elements stripe={stripePromise} options={{ clientSecret: data.clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: accent } } }}>
        <HoldForm amount={data.amount} mode={data.mode} token={token} accent={accent} onDone={() => setDone(true)} />
      </Elements>
    </>
  );
}
